import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  Users, Calculator, FileText, Shield, ArrowRight, Check, MessageCircle,
  DollarSign, BarChart3, CalendarDays, Wallet, Globe, HeartHandshake,
  Facebook, Instagram, Linkedin, Bell, ChevronDown, ChevronRight,
  Rocket, ScrollText, TrendingUp,
} from "lucide-react";
import { trackFullEvent } from "@/lib/tracking";
import heroBusinessman from "@/assets/hero-businessman.png";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu as MenuIcon } from "lucide-react";

const CAKTO_CHECKOUT_URL = "https://pay.cakto.com.br/37uv92w_894290";
const WHATSAPP_TRIAL = "https://wa.me/5547997303469?text=" + encodeURIComponent(
  "Olá! Quero começar o teste grátis de 7 dias do RH Simples. Qual o link de pagamento para os planos Mensal (R$ 34,90) e Anual (R$ 379,90)?"
);

const heroBullets = [
  "Controle de funcionários e faltas",
  "Horas extras, descontos e ocorrências",
  "Cálculo de folha para controle (não fiscal)",
  "Impressão de folha e recibos em PDF",
  "Gestão de custos de pessoal",
];

const features = [
  { icon: Users, title: "Gestão de Funcionários", desc: "Cadastre, organize e mantenha os dados do seu time, incluindo faltas e atrasos.", color: "bg-indigo-100 text-indigo-600" },
  { icon: Wallet, title: "Folha de Pagamento", desc: "Calcule a folha para controle interno com salários, horas extras e descontos.", color: "bg-emerald-100 text-emerald-600" },
  { icon: BarChart3, title: "Custos e Relatórios", desc: "Saiba exatamente o custo real de cada funcionário com relatórios claros.", color: "bg-amber-100 text-amber-600" },
  { icon: FileText, title: "Ocorrências e Documentos", desc: "Registre advertências, suspensões e gere declarações com facilidade.", color: "bg-sky-100 text-sky-600" },
  { icon: CalendarDays, title: "Férias e Afastamentos", desc: "Controle períodos de férias e licenças de forma simples e visual.", color: "bg-rose-100 text-rose-600" },
  { icon: Shield, title: "Seguro e Privado", desc: "Seus dados de gestão protegidos com a melhor tecnologia.", color: "bg-violet-100 text-violet-600" },
];

const trustBand = [
  { icon: Globe, title: "100% online", desc: "Acesse de qualquer lugar" },
  { icon: Shield, title: "Seguro e confiável", desc: "Seus dados protegidos" },
  { icon: HeartHandshake, title: "Suporte humano", desc: "Atendimento de verdade" },
];

const testimonials = [
  {
    quote: "Antes levava horas para fechar a folha. Hoje faço tudo em minutos. O RH Simples mudou a nossa rotina!",
    name: "Juliana Martins",
    role: "Financeiro",
    company: "Padaria Sabor & Arte",
  },
  {
    quote: "Os relatórios de custos nos ajudaram a tomar decisões melhores e planejar o crescimento da empresa.",
    name: "Carlos Eduardo",
    role: "Sócio Administrador",
    company: "Oficina Master",
  },
  {
    quote: "Interface super fácil de usar e suporte sempre atencioso. Recomendo para qualquer pequeno negócio.",
    name: "Mariana Oliveira",
    role: "Departamento Pessoal",
    company: "Clínica Bem-Estar",
  },
];

const logos = [
  "Padaria Sabor & Arte",
  "Clínica Master",
  "Mercado Central",
  "Pet Shop Amigo Fiel",
  "Studio Beleza & Co",
];

const plans = [
  {
    name: "Mensal",
    tagline: "Controle completo para sua empresa.",
    price: "34,90",
    employees: "Ilimitado",
    features: [
      "Controle de Funcionários",
      "Lançamento de Faltas e Horas Extras",
      "Gestão de Ocorrências (Advertências/Suspensões)",
      "Cálculo de Folha (Não Fiscal)",
      "Cálculo de Custo de Funcionário",
      "Impressão de Recibos em PDF",
      "Suporte via WhatsApp",
    ],
    cta: "Começar Agora",
    href: "/cadastro",
    popular: false,
  },
  {
    name: "Anual",
    tagline: "Economize com o plano anual.",
    price: "379,90",
    employees: "Ilimitado",
    features: [
      "Tudo do plano Mensal",
      "Economia de 2 meses",
      "Pagamento único anual",
      "Prioridade no Suporte",
      "Acesso imediato",
    ],
    cta: "Começar Agora",
    href: "/cadastro",
    popular: true,
  },
];

const faqs = [
  { q: "O que está incluso no período de teste grátis?", a: "Acesso completo a todas as funcionalidades do sistema por 7 dias, sem precisar de cartão de crédito." },
  { q: "O sistema é fiscal?", a: "Não. O RH Simples é focado exclusivamente em controle e gestão interna, facilitando o cálculo da folha, custos e gestão de ocorrências de forma gerencial." },
  { q: "Preciso de cartão de crédito para começar?", a: "Não. Você começa o teste grátis sem informar cartão. A cobrança só acontece se você decidir continuar." },
  { q: "Meus dados estão seguros?", a: "Sim. Usamos criptografia, autenticação e isolamento por empresa para garantir total segurança." },
  { q: "Posso cancelar a qualquer momento?", a: "Sim. Não há fidelidade. Você cancela quando quiser, sem multa." },
  { q: "Como funciona o suporte?", a: "Suporte humano via WhatsApp, e-mail e central de ajuda nos dias úteis." },
];

// ── Brand mark ───────────────────────────────────────────────
function BrandMark({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const dims = size === "lg" ? "h-12 w-12" : size === "sm" ? "h-7 w-7" : "h-9 w-9";
  const text = size === "lg" ? "text-3xl" : size === "sm" ? "text-base" : "text-xl";
  return (
    <Link to="/" className="flex items-center gap-2.5">
      <div className={`${dims} rounded-xl bg-primary flex items-center justify-center shadow-sm`}>
        <Users className={size === "lg" ? "h-6 w-6" : "h-5 w-5"} color="white" />
      </div>
      <span className={`${text} font-extrabold tracking-tight text-foreground`}>
        RH <span className="text-primary">Simples</span>
      </span>
    </Link>
  );
}

// ── Dashboard mockup inside browser frame ────────────────────
function DashboardMockup() {
  return (
    <div className="relative rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 overflow-hidden">
      {/* Top chrome */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-white">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <div className="ml-3 flex-1 h-6 rounded-md bg-muted/60" />
      </div>
      <div className="flex">
        {/* Sidebar */}
        <div className="hidden sm:flex w-36 lg:w-44 flex-col gap-1 p-3 border-r border-border bg-white">
          <div className="flex items-center gap-2 mb-3 px-2">
            <div className="h-6 w-6 rounded bg-primary" />
            <span className="text-[11px] font-bold text-foreground">RH Simples</span>
          </div>
          {[
            "Dashboard",
            "Funcionários",
            "Folha de Pagamento",
            "Férias",
            "Relatórios",
            "Custos",
            "Configurações",
          ].map((label, i) => (
            <div
              key={label}
              className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px] ${
                i === 0 ? "bg-indigo-50 text-primary font-semibold" : "text-muted-foreground"
              }`}
            >
              <span className={`h-2 w-2 rounded-sm ${i === 0 ? "bg-primary" : "bg-muted-foreground/40"}`} />
              {label}
            </div>
          ))}
        </div>

        {/* Main */}
        <div className="flex-1 p-3 sm:p-4 bg-[hsl(220_20%_98%)]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-foreground">Dashboard</span>
            <div className="flex items-center gap-2">
              <Bell className="h-3.5 w-3.5 text-muted-foreground" />
              <div className="flex items-center gap-1.5">
                <div className="h-5 w-5 rounded-full bg-primary text-[9px] font-bold text-white flex items-center justify-center">A</div>
                <span className="text-[10px] font-semibold text-foreground hidden md:inline">Administrador</span>
                <ChevronDown className="h-3 w-3 text-muted-foreground hidden md:inline" />
              </div>
            </div>
          </div>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            {[
              { i: Users, c: "bg-indigo-100 text-indigo-600", l: "Funcionários", v: "23", s: "ativos" },
              { i: DollarSign, c: "bg-emerald-100 text-emerald-600", l: "Custo Total (mês)", v: "R$ 27.850", s: "com encargos" },
              { i: Wallet, c: "bg-amber-100 text-amber-600", l: "Folha (mês)", v: "R$ 21.650", s: "salários" },
              { i: FileText, c: "bg-sky-100 text-sky-600", l: "Encargos (mês)", v: "R$ 6.200", s: "INSS, FGTS" },
            ].map((k) => (
              <div key={k.l} className="rounded-lg border border-border bg-white p-2.5">
                <div className={`h-6 w-6 rounded-md flex items-center justify-center mb-1.5 ${k.c}`}>
                  <k.i className="h-3 w-3" />
                </div>
                <p className="text-[9px] font-medium text-muted-foreground leading-tight">{k.l}</p>
                <p className="text-[11px] font-extrabold text-foreground mt-0.5">{k.v}</p>
                <p className="text-[8px] text-muted-foreground">{k.s}</p>
              </div>
            ))}
          </div>
          {/* Chart + summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="sm:col-span-2 rounded-lg border border-border bg-white p-3">
              <p className="text-[10px] font-bold text-foreground mb-1.5">Custo de Pessoal <span className="text-muted-foreground font-medium">(últimos 6 meses)</span></p>
              <svg viewBox="0 0 200 80" className="w-full h-20">
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(239 84% 67%)" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="hsl(239 84% 67%)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d="M0,60 L33,55 L66,45 L100,50 L133,38 L166,32 L200,20 L200,80 L0,80 Z" fill="url(#g1)" />
                <path d="M0,60 L33,55 L66,45 L100,50 L133,38 L166,32 L200,20" stroke="hsl(239 84% 67%)" strokeWidth="2" fill="none" />
                {[[0,60],[33,55],[66,45],[100,50],[133,38],[166,32],[200,20]].map(([x,y],i)=>(
                  <circle key={i} cx={x} cy={y} r="2.5" fill="hsl(239 84% 67%)" />
                ))}
              </svg>
              <div className="flex justify-between text-[8px] text-muted-foreground mt-1">
                <span>Jan</span><span>Fev</span><span>Mar</span><span>Abr</span><span>Mai</span><span>Jun</span>
              </div>
            </div>
            <div className="rounded-lg border border-border bg-white p-3">
              <p className="text-[10px] font-bold text-foreground mb-2">Resumo da Folha</p>
              <div className="space-y-1.5 text-[9px]">
                <div className="flex justify-between"><span className="text-muted-foreground">Salários</span><span className="font-semibold text-foreground">R$ 18.450</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Adicionais</span><span className="font-semibold text-foreground">R$ 2.800</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Descontos</span><span className="font-semibold text-destructive">-R$ 2.400</span></div>
                <div className="flex justify-between pt-1.5 border-t border-border"><span className="font-semibold text-foreground">Total</span><span className="font-extrabold text-primary">R$ 21.650</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  useEffect(() => {
    trackFullEvent("ViewContent", { content_name: "Landing Page" });
  }, []);

  const handleCTAClick = () => {
    trackFullEvent("Lead", { content_name: "Teste Grátis CTA" });
  };
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-card/90 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 lg:px-8">
          <BrandMark />
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-muted-foreground">
            <a href="#recursos" className="hover:text-foreground transition">Recursos</a>
            <a href="#precos" className="hover:text-foreground transition">Preços</a>
            <a href="#depoimentos" className="hover:text-foreground transition">Depoimentos</a>
            <a href="#faq" className="hover:text-foreground transition">Como funciona</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden sm:inline text-sm font-semibold text-foreground hover:text-primary transition">
              Entrar
            </Link>
            <Button asChild className="rounded-xl shadow-sm hidden sm:flex">
              <Link to="/cadastro" onClick={handleCTAClick}>
                Teste grátis por 7 dias
              </Link>
            </Button>
            
            {/* Mobile Menu */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                  <MenuIcon className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle className="text-left">
                    <BrandMark size="sm" />
                  </SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-6 mt-10">
                  <nav className="flex flex-col gap-4 text-lg font-medium">
                    <a href="#recursos" className="hover:text-primary transition py-2 border-b border-border/50">Recursos</a>
                    <a href="#precos" className="hover:text-primary transition py-2 border-b border-border/50">Preços</a>
                    <a href="#depoimentos" className="hover:text-primary transition py-2 border-b border-border/50">Depoimentos</a>
                    <a href="#faq" className="hover:text-primary transition py-2 border-b border-border/50">Como funciona</a>
                  </nav>
                  <div className="flex flex-col gap-3 pt-6">
                    <Button asChild variant="outline" className="rounded-xl h-12">
                      <Link to="/login">Entrar no Sistema</Link>
                    </Button>
                    <Button asChild className="rounded-xl h-12">
                      <Link to="/cadastro" onClick={handleCTAClick}>Começar agora grátis</Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-50/60 via-white to-violet-50/40">
        {/* Decorative purple blob */}
        <div className="pointer-events-none absolute -top-32 -right-20 h-[520px] w-[520px] rounded-full bg-gradient-to-br from-indigo-500/30 to-violet-600/20 blur-3xl" />
        <div className="pointer-events-none absolute top-0 right-0 h-[420px] w-[55%] bg-[radial-gradient(circle_at_70%_30%,rgba(99,102,241,0.18),transparent_60%)]" />

        <div className="container mx-auto px-4 lg:px-8 pt-10 lg:pt-14 pb-10 lg:pb-16 relative">
          {/* Top tagline row */}
          <div className="flex items-center gap-4 mb-8 lg:mb-10">
            <div className="hidden sm:block h-px flex-1 max-w-[60px] bg-border" />
            <p className="text-sm md:text-base text-muted-foreground">
              O RH que cabe no <span className="font-semibold text-primary">seu negócio</span>
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-start">
            {/* LEFT */}
            <div className="relative z-10">
              <h1 className="text-4xl md:text-5xl lg:text-[3.6rem] font-extrabold tracking-tight leading-[1.05] text-foreground">
                Gestão de pessoas{" "}
                <span className="text-primary">simples, completa</span>{" "}
                e sem burocracia.
              </h1>
              <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
                Controle de funcionários, faltas, ocorrências e cálculo de folha gerencial em um só lugar.
                Tudo fácil, com impressão em PDF e foco total na sua gestão.
              </p>

              {/* 4 feature mini-cards */}
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-xl">
                {[
                  { icon: Users, color: "bg-indigo-100 text-indigo-600", title: "Funcionários", desc: "Cadastre e organize sua equipe" },
                  { icon: DollarSign, color: "bg-emerald-100 text-emerald-600", title: "Folha Gerencial", desc: "Cálculo de folha para controle interno com PDF" },
                  { icon: BarChart3, color: "bg-amber-100 text-amber-600", title: "Custos e Relatórios", desc: "Tenha visibilidade total dos seus gastos" },
                  { icon: Shield, color: "bg-violet-100 text-violet-600", title: "Seguro e Confiável", desc: "Seus dados protegidos com segurança" },
                ].map((f) => (
                  <div key={f.title} className="text-center">
                    <div className={`mx-auto h-14 w-14 rounded-2xl ${f.color} flex items-center justify-center shadow-sm mb-3`}>
                      <f.icon className="h-7 w-7" strokeWidth={2.2} />
                    </div>
                    <p className="text-[13px] font-bold text-foreground leading-tight">{f.title}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground leading-snug">{f.desc}</p>
                  </div>
                ))}
              </div>

              {/* CTA + trust pill */}
              <div className="mt-10 flex flex-col sm:flex-row items-stretch gap-4">
               <Link
                  to="/cadastro"
                  className="group flex items-center gap-4 rounded-2xl bg-primary px-6 py-4 text-primary-foreground shadow-xl shadow-primary/30 hover:shadow-primary/40 hover:-translate-y-0.5 transition"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 group-hover:bg-white/30 transition">
                    <ArrowRight className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-base font-extrabold leading-tight">Teste grátis por 7 dias</span>
                    <span className="block text-xs text-white/80">Sem cartão de crédito</span>
                  </span>
                </Link>
                <div className="flex items-center gap-3 rounded-2xl border border-border bg-white px-5 py-3 shadow-sm">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 shrink-0">
                    <Check className="h-5 w-5" strokeWidth={3} />
                  </span>
                  <p className="text-xs leading-tight text-muted-foreground">
                    Mais de <strong className="text-foreground">2.500 empresas</strong>
                    <br /> já simplificam o RH com a gente
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT — dashboard + photo composition */}
            <div className="relative lg:h-[560px]">
              {/* Dashboard mockup (back layer) */}
              <div className="relative lg:absolute lg:top-0 lg:right-0 lg:w-[105%] w-full lg:max-w-none z-10 overflow-hidden rounded-2xl shadow-xl">
                <DashboardMockup />
              </div>

              {/* Floating "Seus dados protegidos" badge */}
              <div className="hidden lg:flex absolute -top-4 right-2 z-30 items-center gap-2 rounded-xl border-2 border-emerald-400 bg-white px-3 py-2 shadow-lg">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                  <Shield className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <p className="text-[11px] font-semibold text-foreground leading-tight">
                  Seus dados<br />protegidos
                </p>
              </div>

              {/* Businessman photo (front layer) */}
              <img
                src={heroBusinessman}
                alt="Profissional usando o RH Simples"
                width={1024}
                height={1024}
                className="hidden lg:block absolute bottom-[-40px] left-1/2 -translate-x-[55%] w-[480px] h-auto z-20 drop-shadow-2xl select-none pointer-events-none"
              />

              {/* Floating "Mais tempo" card */}
              <div className="hidden lg:flex absolute bottom-6 right-0 z-30 items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-xl ring-1 ring-black/5 max-w-[280px]">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shrink-0">
                  <Rocket className="h-6 w-6" />
                </span>
                <p className="text-xs leading-snug text-foreground">
                  Mais tempo para o que realmente importa:{" "}
                  <span className="font-bold text-primary">fazer seu negócio crescer.</span>
                </p>
              </div>
            </div>
          </div>

          {/* Bottom 4-item trust strip */}
          <div className="mt-14 lg:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-border pt-8">
            {[
              { icon: Globe, title: "100% online", desc: "Acesse de qualquer lugar" },
              { icon: HeartHandshake, title: "Suporte humano", desc: "Atendimento de verdade" },
              { icon: ScrollText, title: "Fácil de usar", desc: "Intuitivo e prático" },
              { icon: TrendingUp, title: "Feito para crescer", desc: "Do pequeno ao grande" },
            ].map((t) => (
              <div key={t.title} className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-primary shrink-0">
                  <t.icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-bold text-foreground leading-tight">{t.title}</p>
                  <p className="text-xs text-muted-foreground">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Features */}
      <section id="recursos" className="py-20 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
              Tudo que você precisa para gerir seu time
              <br className="hidden md:inline" /> e seu negócio{" "}
              <span className="text-primary">com tranquilidade</span>
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Funcionalidades completas, interface intuitiva e zero burocracia.
              Mais tempo para o que realmente importa: fazer seu negócio crescer.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div
                key={f.title}
                className="group rounded-2xl border border-border bg-card p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center mb-4 ${f.color}`}>
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="depoimentos" className="py-20 lg:py-24 bg-card border-y border-border">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="inline-flex items-center rounded-full bg-indigo-50 border border-indigo-100 px-3 py-1 text-xs font-semibold text-primary mb-5">
              ● Quem usa, recomenda
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
              Pequenos negócios que simplificaram o RH
              <br className="hidden md:inline" /> e <span className="text-primary">ganharam tempo</span>
            </h2>
            <p className="mt-4 text-muted-foreground">Histórias reais de quem usa e aprova.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="rounded-2xl border border-border bg-background p-6 shadow-sm">
                <p className="text-sm leading-relaxed text-foreground">"{t.quote}"</p>
                <div className="mt-6 pt-5 border-t border-border flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary font-bold text-sm flex items-center justify-center">
                    {t.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role} · {t.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-8 sm:gap-12 opacity-60">
            {logos.map((l) => (
              <span key={l} className="text-sm font-bold text-muted-foreground tracking-wide">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="precos" className="py-20 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-flex items-center rounded-full bg-indigo-50 border border-indigo-100 px-3 py-1 text-xs font-semibold text-primary mb-5">
              ● Planos simples e justos
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              Escolha o plano <span className="text-primary">ideal para o seu negócio</span>
            </h2>
            <p className="mt-3 text-muted-foreground">Sem fidelidade. Cancele quando quiser.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((p) => (
              <div
                key={p.name}
                className={`relative rounded-2xl p-7 transition-all ${
                  p.popular
                    ? "bg-primary text-primary-foreground shadow-2xl shadow-primary/30 scale-[1.02]"
                    : "bg-card border border-border shadow-sm hover:shadow-md"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-white text-primary text-[11px] font-bold px-3 py-1 shadow-md">
                    ★ Mais escolhido
                  </span>
                )}
                <h3 className={`text-xl font-extrabold ${p.popular ? "" : "text-foreground"}`}>{p.name}</h3>
                <p className={`text-xs mt-1 ${p.popular ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                  {p.tagline}
                </p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold tracking-tight">R$ {p.price}</span>
                  <span className={`text-sm ${p.popular ? "text-primary-foreground/80" : "text-muted-foreground"}`}>/mês</span>
                </div>
                <p className={`text-xs mt-1 ${p.popular ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                  Até {p.employees} funcionários
                </p>
                <Button
                  asChild
                  className={`w-full mt-6 rounded-xl ${
                    p.popular
                      ? "bg-white text-primary hover:bg-white/90"
                      : "bg-transparent border border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  }`}
                >
                  <Link to="/cadastro">{p.cta}</Link>
                </Button>
                <ul className="mt-6 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check className={`h-4 w-4 shrink-0 ${p.popular ? "text-white" : "text-primary"}`} strokeWidth={3} />
                      <span className={p.popular ? "" : "text-foreground"}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 lg:py-24 bg-card border-y border-border">
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <h2 className="text-center text-3xl md:text-4xl font-extrabold tracking-tight">
            Dúvidas <span className="text-primary">frequentes</span>
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-x-8 gap-y-3">
            {faqs.map((item, i) => (
              <Accordion key={i} type="single" collapsible>
                <AccordionItem value={`q-${i}`} className="border border-border bg-background rounded-xl px-4">
                  <AccordionTrigger className="text-sm font-semibold text-foreground hover:no-underline py-4">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="rounded-3xl bg-primary text-primary-foreground p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl shadow-primary/20">
            <div className="flex items-start md:items-center gap-4">
              <div className="h-14 w-14 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                <Users className="h-7 w-7" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-extrabold leading-tight">
                  Pronto para simplificar o RH da sua empresa?
                </h3>
                <p className="text-sm text-primary-foreground/80 mt-1">
                  Teste grátis por 7 dias e veja como é fácil gerir pessoas e custos.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-stretch md:items-end gap-2 w-full md:w-auto">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 rounded-xl gap-2 shadow-md">
                <Link to="/cadastro">
                  Começar agora grátis <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <p className="text-[11px] text-primary-foreground/70 text-center md:text-right">
                Não precisa de cartão de crédito
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card">
        <div className="container mx-auto px-4 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2">
            <BrandMark />
            <p className="mt-4 text-sm text-muted-foreground max-w-xs">
              O sistema de RH simples, completo e acessível para pequenos negócios.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a className="h-9 w-9 rounded-lg bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition" href="#"><Facebook className="h-4 w-4" /></a>
              <a className="h-9 w-9 rounded-lg bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition" href="#"><Instagram className="h-4 w-4" /></a>
              <a className="h-9 w-9 rounded-lg bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition" href="#"><Linkedin className="h-4 w-4" /></a>
            </div>
          </div>
          <div>
            <p className="text-sm font-bold text-foreground mb-4">Produto</p>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li><a href="#recursos" className="hover:text-primary">Recursos</a></li>
              <li><a href="#precos" className="hover:text-primary">Preços</a></li>
              <li><a href="#faq" className="hover:text-primary">Como funciona</a></li>
            </ul>
          </div>
          <div>
            <p className="text-sm font-bold text-foreground mb-4">Empresa</p>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li><a href="#" className="hover:text-primary">Sobre nós</a></li>
              <li><a href="https://wa.me/5547997303469" target="_blank" rel="noopener noreferrer" className="hover:text-primary">Contato</a></li>
            </ul>
          </div>
          <div>
            <p className="text-sm font-bold text-foreground mb-4">Suporte</p>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li><a href="#faq" className="hover:text-primary">Central de ajuda</a></li>
              <li><a href="https://wa.me/5547997303469" target="_blank" rel="noopener noreferrer" className="hover:text-primary">Fale conosco</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="container mx-auto px-4 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
            <p>© 2026 RH Simples. Todos os direitos reservados.</p>
            <div className="flex items-center gap-5">
              <a href="#" className="hover:text-primary">Privacidade</a>
              <a href="#" className="hover:text-primary">Termos de uso</a>
              <a href="#" className="hover:text-primary">Segurança</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky WhatsApp helper */}
      <a
        href="https://wa.me/5547997303469?text=Olá! Tenho uma dúvida sobre o RH Simples."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-[#25D366] text-white shadow-xl flex items-center justify-center hover:scale-105 transition"
        aria-label="Falar no WhatsApp"
      >
        <MessageCircle className="h-7 w-7" />
      </a>
    </div>
  );
}
